import React from 'react';
import Dropdown from '../ui/Dropdown';
import { formatDateDayMonth, toLocalISO } from '../../utils/dateFormat';
import { CREW_STATE_PILL_COLOURS, STATUS_PILL_COLOURS } from '../../utils/timesheetDisplay';

/**
 * MY TIMESHEET'S PHONE STATUS PILL (owner 2026-11-01, "yes build it", mock `mock-crew-pill.html`):
 * below `md` the status pill and its separate note become ONE pill beside the completion bar.
 * EXACTLY FOUR pills:
 *   1. **Draft** — every pre-submit state (Conflicting / Rate missing / Reconciled / Away too).
 *   2. **Submitted 31 Oct** + OPEN padlock — sent, the buyer has not acted (still theirs).
 *   3. **Submitted 31 Oct** + CLOSED padlock — sent and acted on, or taken over by the buyer.
 *   4. **Approved** + CLOSED padlock.
 * A pill with a padlock is a BUTTON (`ui/Dropdown`, a sheet below `md`): tap shows the desktop
 * note's own sentence (who / when); its accessible name carries that sentence; Esc / tap outside
 * close. Desktop renders none of this — `CrewTimesheet`'s status line there is unchanged.
 *
 * 🪤 The date is the TIMESTAMP's LOCAL day (`toLocalISO(new Date(ts))`), never its UTC date part —
 * a 00:30 BST submit belongs to that day, not the one before. A taken-over week was never sent,
 * so its date is when the buyer took it (`taken_at`) — owner ruling: both locked states read
 * "Submitted". The pill keeps `data-testid="crew-week-status"`.
 */

/** The phone pill's model: `{ text, cls, lock: null | 'open' | 'closed', sentence }`. */
export function crewPhonePill({ person, submitted, crewState, sentOpen, note }) {
  const day = (ts) => (ts ? formatDateDayMonth(toLocalISO(new Date(ts))) : '');
  if (submitted) return { text: 'Approved', cls: STATUS_PILL_COLOURS.submitted, lock: 'closed', sentence: note };
  if (crewState === 'sent' || crewState === 'taken') {
    return {
      text: `Submitted ${day(person.sent_at || person.taken_at)}`.trim(),
      cls: CREW_STATE_PILL_COLOURS.sent,
      lock: sentOpen ? 'open' : 'closed',
      sentence: note,
    };
  }
  return { text: 'Draft', cls: CREW_STATE_PILL_COLOURS.draft, lock: null, sentence: null };
}

/** The padlock: an open or closed shackle, `currentColor`, decorative (the name carries the state). */
export function Padlock({ open }) {
  return (
    <svg viewBox="0 0 16 16" className="h-3 w-3 shrink-0" aria-hidden="true" data-padlock={open ? 'open' : 'closed'}>
      <rect x="3" y="7" width="10" height="7" rx="1.5" fill="currentColor" />
      <path d={open ? 'M5.5 7V4.8a2.5 2.5 0 0 1 4.9-.6' : 'M5.5 7V5a2.5 2.5 0 0 1 5 0v2'} stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" />
    </svg>
  );
}

const PILL = 'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium';
// A 44px tap target around a pill that LOOKS small (the house phone floor; OrderItemReceivedControls' device).
const TAP = "relative before:absolute before:content-[''] before:-inset-y-3 before:-inset-x-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500";

export default function CrewPhoneStatusPill({ pill }) {
  if (!pill.lock) {
    return <span data-testid="crew-week-status" className={`${PILL} ${pill.cls}`}>{pill.text}</span>;
  }
  return (
    <Dropdown
      haspopup="dialog"
      hideChevron
      panelClassName="w-64 p-3"
      triggerClassName={`${PILL} ${TAP} ${pill.cls}`}
      triggerTestId="crew-week-status"
      title={pill.sentence}
      ariaLabel="Week status"
      label={(
        <>
          {pill.text}
          <Padlock open={pill.lock === 'open'} />
          <span className="sr-only">. {pill.sentence}</span>
        </>
      )}
    >
      <p data-testid="crew-week-status-detail" className="text-sm text-gray-800 dark:text-gray-100">{pill.sentence}</p>
    </Dropdown>
  );
}
